import {useCallback, useEffect, useState} from 'react';

import {Accordion, Button, Checkbox, Modal, Text} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {AccordionRoot} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import {buildFilterState, getFilterReference} from './ActionPlanFilterModal.utils';

import {
  ActionPlanFilterReference,
  ActionPlanFilters,
  ActionPlanFilterState,
} from './ActionPlanFilterModal.types';

import * as css from './ActionPlanFilterModal.css';

/**
 * `ActionPlanFilterModal` renders a modal that allows users to apply filters
 * to a list of adjustments that may be included in their Action Plan.
 */
export interface ActionPlanFilterProps {
  adjustments: ActionPlanPage.ActionPlanAdjustment[];
  filters: ActionPlanFilters;
  setFilters: (filters: ActionPlanFilters) => void;
}
export const ActionPlanFilterModal = ({
  adjustments,
  filters,
  setFilters,
}: ActionPlanFilterProps) => {
  const [touched, setTouched] = useState<boolean>(false);
  const [filterReference, setFilterReference] = useState<ActionPlanFilterReference>();
  const [filterState, setFilterState] = useState<ActionPlanFilterState>();

  useEffect(() => {
    if (!adjustments.length) {
      return;
    }
    setFilterReference(getFilterReference(adjustments));
  }, [adjustments]);

  useEffect(() => {
    if (!filterReference) {
      return;
    }
    setFilterState(buildFilterState(filterReference, filters));
  }, [filterReference, filters]);

  const onCheck = useCallback(
    ({categoryId, impactLevelId}: {categoryId?: string; impactLevelId?: string}) => {
      if (!touched) {
        setTouched(true);
      }
      if (!filterState) {
        return;
      }
      if (categoryId) {
        setFilterState({
          ...filterState,
          categories: filterState.categories.map(({id, selected, title}) => ({
            id,
            selected: id === categoryId ? !selected : selected,
            title,
          })),
        });
      }
      if (impactLevelId) {
        setFilterState({
          ...filterState,
          impactLevels: filterState.impactLevels.map(({id, selected, title}) => ({
            id,
            selected: id === impactLevelId ? !selected : selected,
            title,
          })),
        });
      }
    },
    [filterState],
  );

  const onApplyFilters = useCallback(() => {
    if (!filterState) {
      return;
    }
    setFilters({
      categories: filterState.categories.reduce((categories, {id, selected}) => {
        if (selected) {
          categories.push(id);
        }
        return categories;
      }, [] as string[]),
      impactLevels: filterState.impactLevels.reduce((impactLevels, {id, selected}) => {
        if (selected) {
          impactLevels.push(id);
        }
        return impactLevels;
      }, [] as string[]),
    });
  }, [filterState, setFilters]);

  const onCancel = useCallback(() => {
    if (filterReference) {
      setFilterState(buildFilterState(filterReference, filters));
    }
    setTouched(false);
  }, [filterReference, filters]);

  return (
    <Modal
      header={
        <Text
          className={sprinkles({flex: 'flex', h: 'full', items: 'center'})}
          color="doveGray"
          variant="heading4">
          Filter Recommendations
        </Text>
      }
      footer={
        <div className={sprinkles({flex: 'flex', dir: ['colReverse', 'row'], gap: 8, w: 'full'})}>
          <Button as={ModalClose} variant="secondary" onClick={onCancel}>
            Cancel
          </Button>

          <Button as={ModalClose} onClick={onApplyFilters} disabled={!touched}>
            Apply
          </Button>
        </div>
      }
      layout="drawer">
      <div className={sprinkles({dir: 'col', flex: 'flex', gap: 24})}>
        <AccordionRoot type="single" defaultValue="-1" collapsible>
          <Accordion
            triggerClassName={css.accordionTrigger}
            trigger={<Text variant="heading4">Health Category</Text>}
            value="0">
            <ul className={sprinkles({pX: [0, 24], pB: 24})}>
              {filterState &&
                filterState.categories.map(({id, selected, title}) => (
                  <li
                    className={sprinkles({flex: 'flex', items: 'center', gap: 16, pY: 16})}
                    key={id}>
                    <Checkbox checked={selected} onChange={() => onCheck({categoryId: id})} />
                    <Text fontWeight="regular" color="doveGray" variant="buttonSmall">
                      {title}
                    </Text>
                  </li>
                ))}
            </ul>
          </Accordion>

          <Accordion
            triggerClassName={css.accordionTrigger}
            trigger={<Text variant="heading4">Impact Level</Text>}
            value="1">
            <ul className={sprinkles({pX: [0, 24], pB: 24})}>
              {filterState &&
                filterState.impactLevels.map(({id, selected, title}) => (
                  <li
                    className={sprinkles({flex: 'flex', items: 'center', gap: 16, pY: 16})}
                    key={id}>
                    <Checkbox checked={selected} onChange={() => onCheck({impactLevelId: id})} />
                    <Text fontWeight="regular" color="doveGray" variant="buttonSmall">
                      {title}
                    </Text>
                  </li>
                ))}
            </ul>
          </Accordion>
          <Accordion value="-1" className={sprinkles({display: 'none'})} />
        </AccordionRoot>
      </div>
    </Modal>
  );
};
